import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, X, Zap } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EVENT_TRIGGERS, getGroupColor } from '@/lib/queues';
import { cn } from '@/lib/utils';

// Pick which (swimlane › sub-category) the event slots below edit. Exported so other tabs (e.g.
// the doctor-waiting/consultation stage pickers in the Swimlanes tab) can reuse the exact same
// lane › sub-category hierarchy picker instead of building a new one.
export function SubcategoryPicker({
  swimlanes,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a sub-category',
  allowNone = false,
  noneLabel = 'None selected',
}) {
  const [open, setOpen] = useState(false);
  const lane = swimlanes.find((l) => l.id === value.laneId);
  const sub = lane?.subCategories?.find((s) => s.id === value.subId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-[13px] font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
        >
          {lane && sub ? (
            <>
              <span className={cn('h-2 w-2 rounded-full', getGroupColor(lane.colorId).dotClassName)} />
              <span>{lane.name || 'Untitled'}</span>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              <span className={cn('h-2 w-2 rounded-full', getGroupColor(sub.colorId).dotClassName)} />
              <span>{sub.name || 'Untitled'}</span>
            </>
          ) : (
            <span className="text-muted-foreground">{allowNone ? noneLabel : placeholder}</span>
          )}
          <ChevronDown className="ml-1 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <ScrollArea viewportClassName="max-h-80 p-1.5">
        {allowNone ? (
          <button
            type="button"
            onClick={() => {
              onChange({ laneId: null, subId: null });
              setOpen(false);
            }}
            className={cn(
              'mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] italic text-muted-foreground hover:bg-accent',
              !lane || !sub ? 'bg-accent font-semibold not-italic' : '',
            )}
          >
            {noneLabel}
          </button>
        ) : null}
        {swimlanes.map((l) => (
          <div key={l.id} className="mb-1">
            <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {l.name || 'Untitled'}
            </div>
            {(l.subCategories || []).map((s) => {
              const active = value.laneId === l.id && value.subId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    onChange({ laneId: l.id, subId: s.id });
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent',
                    active ? 'bg-accent font-semibold' : '',
                  )}
                >
                  <span className={cn('h-2 w-2 rounded-full', getGroupColor(s.colorId).dotClassName)} />
                  {s.name || 'Untitled'}
                </button>
              );
            })}
            {(l.subCategories || []).length === 0 ? (
              <div className="px-2 py-1 text-[12px] italic text-muted-foreground">No sub-categories</div>
            ) : null}
          </div>
        ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function EventSlot({ label, hint, keys, disabled, onAdd, onRemove }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const value = draft.trim();
    if (value) onAdd(value);
    setDraft('');
  };

  return (
    <div className="rounded-lg border border-border bg-background px-3.5 py-3">
      <div className="flex items-center gap-2">
        <span className="text-[12.5px] font-extrabold text-foreground">{label}</span>
        <span className="text-[11.5px] text-muted-foreground">{hint}</span>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {keys.length === 0 ? (
          <span className="text-xs italic text-muted-foreground">No events fired</span>
        ) : (
          keys.map((key) => (
            <span
              key={key}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary/15 px-2 py-1 font-mono text-[12px] font-bold text-primary ring-1 ring-primary/40"
            >
              {key}
              {!disabled ? (
                <button
                  type="button"
                  onClick={() => onRemove(key)}
                  aria-label={`Remove ${key}`}
                  className="opacity-70 transition hover:opacity-100"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              ) : null}
            </span>
          ))
        )}
        {!disabled ? (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              }
            }}
            onBlur={commit}
            placeholder="add event key"
            className="h-7 w-40 rounded-md border border-dashed border-border bg-transparent px-2 font-mono text-[12px] text-foreground placeholder:font-sans placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        ) : null}
      </div>
    </div>
  );
}

// The per-queue Transition events tab (config only): pick a sub-category, then edit the event
// keys fired onCheckIn / onEnter / onExit. Plugins subscribe to these keys in the runtime
// (built later) — no live plugin status here.
export function TransitionEventsSection({ swimlanes, disabled = false, onSetEventKeys }) {
  const firstLane = swimlanes[0];
  const firstSub = firstLane?.subCategories?.[0];
  const [selection, setSelection] = useState(() => ({
    laneId: firstLane?.id ?? null,
    subId: firstSub?.id ?? null,
  }));

  // Resolve the selection against the current draft, falling back to the first available
  // sub-category so a delete/rename never leaves the slots pointing at nothing.
  const { lane, sub } = useMemo(() => {
    const resolvedLane =
      swimlanes.find((l) => l.id === selection.laneId && l.subCategories?.length) ||
      swimlanes.find((l) => l.subCategories?.length) ||
      null;
    const resolvedSub =
      resolvedLane?.subCategories?.find((s) => s.id === selection.subId) ||
      resolvedLane?.subCategories?.[0] ||
      null;
    return { lane: resolvedLane, sub: resolvedSub };
  }, [swimlanes, selection]);

  if (!lane || !sub) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-8 text-center text-sm text-muted-foreground">
        Add a swimlane with at least one sub-category to configure transition events.
      </div>
    );
  }

  const events = sub.events || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[12.5px] font-semibold text-muted-foreground">Sub-category</span>
        <SubcategoryPicker
          swimlanes={swimlanes}
          value={{ laneId: lane.id, subId: sub.id }}
          onChange={setSelection}
        />
      </div>

      <div className="flex flex-col gap-2.5">
        {EVENT_TRIGGERS.map((trigger) => (
          <EventSlot
            key={trigger.key}
            label={trigger.label}
            hint={trigger.hint}
            keys={Array.isArray(events[trigger.key]) ? events[trigger.key] : []}
            disabled={disabled}
            onAdd={(value) => {
              const current = Array.isArray(events[trigger.key]) ? events[trigger.key] : [];
              if (current.includes(value)) return;
              onSetEventKeys(lane.id, sub.id, trigger.key, [...current, value]);
            }}
            onRemove={(value) => {
              const current = Array.isArray(events[trigger.key]) ? events[trigger.key] : [];
              onSetEventKeys(
                lane.id,
                sub.id,
                trigger.key,
                current.filter((k) => k !== value),
              );
            }}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
        <Zap className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        Events publish to a dedicated queue stream, separate from messages. Plugins subscribe to
        these keys and react.
      </div>
    </div>
  );
}
